"use client";

import { useState } from "react";
import clsx from "clsx";
import { SignDocumentModal } from "@/components/sign/SignDocumentModal";
import { useTranslations } from "@/lib/compat/i18n";

/**
 * Insurance Certificate Anchor tile — the critical post-shipment HCS event
 * in the OrbitraOS Trace pillar.
 *
 * Per the spec, an insurance certificate MUST be anchored AFTER the
 * shipment event (Loading milestone) — an out-of-sequence attempt is a
 * blocking violation. This tile makes the sequencing visible to the audience.
 *
 * For CIF/CIP (T1, T2): seller-arranged, mandatory under UCP 600 Art. 28
 * For FOB (T3):         buyer-arranged, not UCP 28 mandatory but still
 *                       anchored for the buyer's records and BR-side
 *                       compliance audit.
 */
export function InsuranceCertAnchor({
  shipmentLoaded,
  policyType = "Marine cargo",
  coverage = "≥ 110% CIF",
  insurerName = "Mapfre Brasil",
  lossPayee,
  arrangedBy,
  mandatoryUcp28 = true,
  sequenceNumber = "06",
  viewCertificatePdf,
  viewCertificateRef,
  className,
}: {
  shipmentLoaded: boolean;
  policyType?: string;
  coverage?: string;
  insurerName?: string;
  lossPayee: string;
  arrangedBy: string;
  mandatoryUcp28?: boolean;
  sequenceNumber?: string;
  /** When set, the bottom "anchored ✓" line is replaced with a
   *  "View my insurance certificate" CTA that opens the PDF view-only. */
  viewCertificatePdf?: string;
  viewCertificateRef?: string;
  className?: string;
}) {
  const t = useTranslations("insuranceCertAnchor");
  const anchored = shipmentLoaded; // Anchored when (and only when) shipment has loaded.
  const [certOpen, setCertOpen] = useState(false);
  return (
    <div className={clsx("glass p-4", className)}>
      <div className="flex items-center justify-between">
        <div>
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-traced">{t("kicker")}</p>
          <h3 className="mt-1 font-display text-base font-semibold text-orbitra-paper">{policyType} · {coverage}</h3>
        </div>
        {mandatoryUcp28 ? (
          <span className="rounded-full border border-state-financed/40 bg-state-financed/10 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-state-financed">
            {t("ucpMandatoryBadge")}
          </span>
        ) : (
          <span className="rounded-full border border-orbitra-muted/40 bg-orbitra-muted/10 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-orbitra-muted">
            {t("buyerArrangedBadge")}
          </span>
        )}
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
        <Cell label={t("fieldInsurer")}     value={insurerName} />
        <Cell label={t("fieldLossPayee")}  value={lossPayee} />
        <Cell label={t("fieldArrangedBy")} value={arrangedBy} />
        <Cell label={t("fieldCoverage")}   value={coverage} />
      </div>

      {viewCertificatePdf && anchored ? (
        <button
          type="button"
          onClick={() => setCertOpen(true)}
          className="cta-primary mt-3 w-full py-2 text-[12px] font-semibold"
        >
          {t("viewCertCta")}
        </button>
      ) : (
        <div className={clsx(
          "mt-3 flex items-center gap-3 rounded-md border px-3 py-2 text-[11px]",
          anchored
            ? "border-policy-pass/40 bg-policy-pass/10 text-policy-pass"
            : "border-orbitra-muted/30 bg-orbitra-ink/40 text-orbitra-muted",
        )}>
          <span className="font-mono text-[10px]">
            {anchored ? t("anchoredStatus") : t("queuedStatus")}
          </span>
          <span>·</span>
          <span
            className="font-mono text-[10px] cursor-help"
            title={`HCS topic · ledger-0.4801250 · seq ${sequenceNumber} · anchored content hash sha256:b21d…4e83`}
          >
            {(t as (k: string, p?: Record<string, string | number>) => string)("ledgerRef", { seqNum: sequenceNumber })}
          </span>
          <span className="ms-auto font-mono text-[9px] opacity-80">
            {anchored ? t("postShipmentVerified") : t("anchorBlocksNote")}
          </span>
        </div>
      )}

      {viewCertificatePdf && (
        <SignDocumentModal
          id="insurance-cert-view"
          open={certOpen}
          onOpen={() => setCertOpen(true)}
          onClose={() => setCertOpen(false)}
          pdfPath={viewCertificatePdf}
          documentTitle={t("documentTitle")}
          documentRef={viewCertificateRef ?? t("documentRef")}
          signedAs={insurerName}
          signerOrg={(t as (k: string, p?: Record<string, string | number>) => string)("signerOrg", { insurerName, lossPayee })}
          viewOnly
          actionLabel={t("closeButton")}
        />
      )}
    </div>
  );
}

function Cell({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-state-traced/20 bg-orbitra-ink/40 px-3 py-2">
      <p className="text-[9px] uppercase tracking-[0.18em] text-orbitra-muted">{label}</p>
      <p className="mt-0.5 text-orbitra-paper">{value}</p>
    </div>
  );
}
